/**
 * Available categories for Signature Journeys
 * These categories help travelers find trips that match their interests and demographics
 */

export interface Category {
  id: string;
  label: string;
  description: string;
  icon?: string;
  color?: string;
}

export const JOURNEY_CATEGORIES: Category[] = [
  // Adventure & Activity Level
  {
    id: 'adventure',
    label: 'Adventure Travel',
    description: 'High-energy activities, hiking, trekking, and outdoor adventures',
    color: 'bg-orange-100 text-orange-800'
  },
  {
    id: 'expeditions',
    label: 'Expeditions',
    description: 'Challenging multi-day expeditions and remote adventures',
    color: 'bg-red-100 text-red-800'
  },
  {
    id: 'off-beaten-path',
    label: 'Off the Beaten Path',
    description: 'Unique destinations and hidden gems away from tourist crowds',
    color: 'bg-purple-100 text-purple-800'
  },

  // Demographics & Age Groups
  {
    id: 'babies-0-2',
    label: 'Babies (0–2)',
    description: 'Infant-friendly — strollers, cribs, short transfers',
    color: 'bg-pink-100 text-pink-800'
  },
  {
    id: 'young-kids-3-5',
    label: 'Young Kids (3–5)',
    description: 'Playful, short days, pools, easy walks',
    color: 'bg-rose-100 text-rose-800'
  },
  {
    id: 'kids-6-12',
    label: 'Kids (6–12)',
    description: 'Hands-on activities, kid clubs, light adventure',
    color: 'bg-fuchsia-100 text-fuchsia-800'
  },
  {
    id: 'teens-13-17',
    label: 'Teens (13–17)',
    description: 'Higher adventure, freedom time, social vibe',
    color: 'bg-purple-100 text-purple-800'
  },
  {
    id: 'students-gap-year-18-25',
    label: 'Young Adults (18–25)',
    description: 'Social experiences, flexibility, adventure, and nightlife',
    color: 'bg-indigo-100 text-indigo-800'
  },
  {
    id: 'young-professionals-26-39',
    label: 'Young Professionals (26–39)',
    description: 'Adventure + comfort, unique stays',
    color: 'bg-blue-100 text-blue-800'
  },
  {
    id: 'mature-explorers-40-59',
    label: 'Mature Explorers (40–59)',
    description: 'Comfort, culture, food/wine, moderate pace',
    color: 'bg-cyan-100 text-cyan-800'
  },
  {
    id: 'seniors-60-plus',
    label: 'Seniors (60+)',
    description: 'Slower pace, accessibility, medical proximity, fewer one-nighters',
    color: 'bg-teal-100 text-teal-800'
  },

  // Travel Themes
  {
    id: 'cultural',
    label: 'Cultural',
    description: 'Immersive cultural experiences, local traditions, and heritage sites',
    color: 'bg-yellow-100 text-yellow-800'
  },
  {
    id: 'historical-heritage',
    label: 'Historical & Heritage',
    description: 'Ancient ruins, UNESCO sites, archaeological discoveries, and living traditions',
    color: 'bg-amber-100 text-amber-800'
  },
  {
    id: 'music-art',
    label: 'Music & Art',
    description: 'Art galleries, museums, concerts, street art, and cultural performances',
    color: 'bg-violet-100 text-violet-800'
  },
  {
    id: 'wellness',
    label: 'Wellness & Mindfulness',
    description: 'Relaxation, spa experiences, yoga, meditation, and wellness activities',
    color: 'bg-teal-100 text-teal-800'
  },
  {
    id: 'women-travel',
    label: "Women's Travel",
    description: 'Empowering journeys for women with female-led experiences and safe environments',
    color: 'bg-rose-100 text-rose-800'
  },
  {
    id: 'slow-travel',
    label: 'Slow Travel & Nature',
    description: 'Extended stays, deep local connections, and immersive nature experiences',
    color: 'bg-lime-100 text-lime-800'
  },
  {
    id: 'self-guided',
    label: 'Self-Guided',
    description: 'Independent travel with curated itineraries, maps, and local support when needed',
    color: 'bg-slate-100 text-slate-800'
  },
  {
    id: 'couples-romantic',
    label: "Couples' Retreats",
    description: 'Romantic experiences, intimate settings, and relationship-building activities',
    color: 'bg-fuchsia-100 text-fuchsia-800'
  },
  {
    id: 'festivals',
    label: 'Festival Experiences',
    description: 'Local celebrations, traditional festivals, and cultural events',
    color: 'bg-sky-100 text-sky-800'
  },
  {
    id: 'culinary',
    label: 'Culinary Journeys',
    description: 'Food experiences, cooking classes, market tours, and local cuisine',
    color: 'bg-orange-100 text-orange-800'
  },
  {
    id: 'honeymoon',
    label: 'Honeymoon',
    description: 'Romantic escapes perfect for newlyweds and celebrating love',
    color: 'bg-pink-100 text-pink-800'
  },
  {
    id: 'digital-nomads',
    label: 'Digital Nomads',
    description: 'Work-friendly destinations with reliable wifi, coworking spaces, and flexible itineraries',
    color: 'bg-cyan-100 text-cyan-800'
  },

  // Accessibility
  {
    id: 'wheelchair-accessible',
    label: 'Accessible Travel',
    description: 'Accessible accommodations, transportation, and activities',
    color: 'bg-indigo-100 text-indigo-800'
  },

  // Activity Level
  {
    id: 'easy',
    label: 'Easy Pace',
    description: 'Relaxed schedule, minimal physical activity, comfortable accommodations',
    color: 'bg-green-100 text-green-800'
  },
  {
    id: 'moderate',
    label: 'Moderate Activity',
    description: 'Some physical activity, walking tours, light hiking',
    color: 'bg-yellow-100 text-yellow-800'
  },
  {
    id: 'challenging',
    label: 'Challenging',
    description: 'High physical activity, demanding terrain, endurance required',
    color: 'bg-red-100 text-red-800'
  },

  // Special Interests
  {
    id: 'photography',
    label: 'Photography Tours',
    description: 'Perfect for photographers, scenic viewpoints, and photo opportunities',
    color: 'bg-gray-100 text-gray-800'
  },
  {
    id: 'volunteering',
    label: 'Volunteering Trips',
    description: 'Give back while traveling through community projects and meaningful work',
    color: 'bg-green-100 text-green-800'
  },
  {
    id: 'unusual',
    label: 'Unusual',
    description: 'Unique, quirky, and unconventional travel experiences',
    color: 'bg-purple-100 text-purple-800'
  },
  {
    id: 'wildlife',
    label: 'Wildlife & Nature',
    description: 'Wildlife watching, nature reserves, and environmental experiences',
    color: 'bg-emerald-100 text-emerald-800'
  },
  {
    id: 'luxury',
    label: 'Luxury',
    description: 'Premium accommodations, exclusive experiences, and high-end services',
    color: 'bg-gold-100 text-gold-800'
  },
  {
    id: 'family-kids',
    label: "Family & Kid's Travel",
    description: 'Family-friendly activities, kid-focused experiences, and multi-generational fun',
    color: 'bg-amber-100 text-amber-800'
  }
];

/**
 * Get category by ID
 */
export function getCategoryById(id: string): Category | undefined {
  return JOURNEY_CATEGORIES.find(category => category.id === id);
}

/**
 * Get multiple categories by IDs
 */
export function getCategoriesByIds(ids: string[]): Category[] {
  return ids.map(id => getCategoryById(id)).filter(Boolean) as Category[];
}

/**
 * Get all category IDs
 */
export function getAllCategoryIds(): string[] {
  return JOURNEY_CATEGORIES.map(category => category.id);
}

/**
 * Validate if category IDs are valid
 */
export function validateCategoryIds(ids: string[]): boolean {
  return ids.every(id => JOURNEY_CATEGORIES.some(category => category.id === id));
}

/**
 * Age-related category IDs
 * Includes variations to handle data inconsistencies
 */
export const AGE_CATEGORY_IDS = [
  'babies-0-2',
  'young-kids-3-5',
  'kids-6-12',
  'teens-13-17',
  'students-gap-year-18-25',
  'young-professionals-26-39',
  'mature-explorers-40-59',
  'seniors-60-plus',
  // Variations that might exist in the database
  '60-plus',
  'seniors',
  'adults-only',
  'family-friendly',
  'senior-friendly'
];

/**
 * Get categories that are age-related
 */
export function getAgeCategoriesByIds(ids: string[]): Category[] {
  return ids
    .filter(id => AGE_CATEGORY_IDS.includes(id))
    .map(id => getCategoryById(id))
    .filter(Boolean) as Category[];
}

/**
 * Get categories that are theme-related (not age-related)
 */
export function getThemeCategoriesByIds(ids: string[]): Category[] {
  return ids
    .filter(id => !AGE_CATEGORY_IDS.includes(id))
    .map(id => getCategoryById(id))
    .filter(Boolean) as Category[];
}
